/**
 * `openVqeFromActiveTab` — the Visual Query Editor entry points that are not
 * inside a Query tab: Ctrl/Cmd+Shift+V, the Command Palette and the main
 * toolbar's More ▾ → Visual Query Editor.
 *
 *  - The active tab is a Query tab → it switches to its Visual view on the
 *    statement under the caret (`toggle`: the shortcut and palette switch a
 *    tab already in the Visual view back to SQL). The switch runs through the
 *    tab's own registered controller (`queryViewModeControllers`), which
 *    flushes the typing debounce, reads the caret live and fires
 *    `feature:vqe_mode_toggle`; a tab still mounting gets the switch once its
 *    controller registers.
 *  - Anything else in front (the start page, another workspace) → a new Query
 *    tab starting in the Visual view — its starter canvas — on the active
 *    tab's connection when it has one (`feature:vqe_open { source, seeded:
 *    false }`).
 *
 * Pure logic with injectable deps for unit testing; the callers are one-liners.
 */

import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { isQueryTab } from '../../core/types/workspaceTypes';
import { requestQueryViewMode } from '../../query-editor/visual-mode/queryViewModeControllers';
import type { QueryViewMode } from '../../core/types/workspaceTypes';
import type { QueryViewModeSource } from '../../query-editor/visual-mode/useQueryViewMode';
import { trackVqeEvent } from '../ux/trackVqeEvent';

type WorkspaceStoreApi = Pick<
  ReturnType<typeof useWorkspaceStore.getState>,
  'tabs' | 'activeTabId' | 'addTab' | 'setActiveTab' | 'getNextQueryNumber'
>;

export type OpenVqeFromActiveTabDeps = {
  workspaceStore: () => WorkspaceStoreApi;
  /** Switch a Query tab's view now, or once its tab has mounted. */
  requestViewMode: (tabId: string, mode: QueryViewMode | 'toggle', source: QueryViewModeSource) => void;
};

/** Fixed, low-cardinality entry-point tags. */
export type VqeOpenSource = 'toolbar' | 'shortcut' | 'palette';

export type OpenVqeFromActiveTabOptions = {
  readonly source?: VqeOpenSource;
  /** A tab already in the Visual view goes back to SQL (shortcut, palette). */
  readonly toggle?: boolean;
};

const defaultDeps: OpenVqeFromActiveTabDeps = {
  workspaceStore: () => useWorkspaceStore.getState(),
  requestViewMode: requestQueryViewMode,
};

export function openVqeFromActiveTab(
  deps: OpenVqeFromActiveTabDeps = defaultDeps,
  options: OpenVqeFromActiveTabOptions = {},
): string {
  const ws = deps.workspaceStore();
  const activeTab = ws.tabs.find((t) => t.id === ws.activeTabId);
  const source = options.source ?? 'toolbar';

  if (activeTab && isQueryTab(activeTab)) {
    deps.requestViewMode(activeTab.id, options.toggle ? 'toggle' : 'visual', source);
    return activeTab.id;
  }

  // Thread the active tab's connection (when it has one) so the starter
  // canvas can list its tables; never invent a fallback connection.
  const newTabId = ws.addTab({
    type: 'query',
    title: `Query-${ws.getNextQueryNumber()}`,
    content: { query: '', viewMode: 'visual', visualStatementOffset: 0 },
    connectionInfo: activeTab?.connectionInfo,
  });
  ws.setActiveTab(newTabId);
  if (options.source) trackVqeEvent('feature:vqe_open', { source: options.source, seeded: false });
  return newTabId;
}
